import { Icon } from "@core/assets";
import type { IconKey } from "@core/assets";
import { cn } from "@core/utils";
import type { ToastSeverity } from "./types";

/**
 * Per-severity feedback presentation: the featured-icon glyph plus the three feedback tokens
 * (icon / ring / ring-outer) that the theme defines for exactly this pattern. All class strings
 * are literal so Tailwind's JIT keeps them.
 */
const TONE: Record<
  ToastSeverity,
  { icon: IconKey; iconColor: string; ring: string; ringOuter: string }
> = {
  success: {
    icon: "check",
    iconColor: "text-(--feedback-success-icon)",
    ring: "border-(--feedback-success-ring)",
    ringOuter: "border-(--feedback-success-ring-outer)",
  },
  error: {
    icon: "alert",
    iconColor: "text-(--feedback-error-icon)",
    ring: "border-(--feedback-error-ring)",
    ringOuter: "border-(--feedback-error-ring-outer)",
  },
  warning: {
    icon: "alert-triangle",
    iconColor: "text-(--feedback-warning-icon)",
    ring: "border-(--feedback-warning-ring)",
    ringOuter: "border-(--feedback-warning-ring-outer)",
  },
  info: {
    icon: "info-circle",
    iconColor: "text-(--feedback-info-icon)",
    ring: "border-(--feedback-info-ring)",
    ringOuter: "border-(--feedback-info-ring-outer)",
  },
};

interface ToastCardProps {
  severity: ToastSeverity;
  /** Resolved (already i18n'd) title line — sm/SemiBold. */
  title: string;
  /** Resolved description line — sm/Regular. Always present (every toast has both lines). */
  description: string;
  /** Dismiss the toast (the X — the only action kept from the design). */
  onDismiss: () => void;
  /** Resolved aria-label for the close button. */
  dismissLabel: string;
}

/**
 * Toast surface matching the Figma "Notification" design: a featured icon (state glyph
 * with two concentric rings), a title + optional description, and an X close. The action row
 * from the design is intentionally omitted. Sonner's `<li>` owns the live-region semantics, so
 * this card carries none.
 */
export function ToastCard({
  severity,
  title,
  description,
  onDismiss,
  dismissLabel,
}: ToastCardProps) {
  const tone = TONE[severity];

  return (
    <div className="flex w-full items-start gap-md rounded-lg border border-(--border-default) bg-surface-card p-md shadow-lg">
      {/* Featured icon — state glyph centered under two concentric rings (grid overlap). */}
      <span
        className={cn(
          "relative grid size-10 shrink-0 place-items-center",
          tone.iconColor
        )}
      >
        <span
          aria-hidden
          className={cn(
            "col-start-1 row-start-1 size-9 rounded-full border",
            tone.ringOuter
          )}
        />
        <span
          aria-hidden
          className={cn(
            "col-start-1 row-start-1 size-[26px] rounded-full border",
            tone.ring
          )}
        />
        <Icon name={tone.icon} size={20} className="col-start-1 row-start-1" />
      </span>

      {/* Content — title + description */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm-semibold text-(--text-primary)">{title}</p>
        <p className="text-sm-regular text-(--text-secondary)">{description}</p>
      </div>

      {/* Close (X) — the one action kept from the design */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={dismissLabel}
        className="grid size-5 shrink-0 place-items-center rounded-xs text-(--text-tertiary) outline-none transition-colors hover:text-(--text-secondary) focus-visible:ring-2 focus-visible:ring-focus-ring"
      >
        <Icon name="close" size={20} />
      </button>
    </div>
  );
}
